import React, { useState, useEffect } from 'react';
import { potentialCustomersAPI } from '../services/api';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { User, Mail, Phone, MoreHorizontal, Plus, Loader2, Pencil, Trash2 } from 'lucide-react';

const PotentialCustomersPage = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  
  // Form state for Add
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [followUpStatus, setFollowUpStatus] = useState('');
  const [notes, setNotes] = useState('');

  // Form state for Edit
  const [editName, setEditName] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [editPhone, setEditPhone] = useState('');
  const [editFollowUpStatus, setEditFollowUpStatus] = useState('');
  const [editNotes, setEditNotes] = useState('');

  useEffect(() => {
    const fetchCustomers = async () => {
      try {
        const response = await potentialCustomersAPI.getAll();
        setCustomers(response.data);
      } catch (error) {
      } finally {
        setLoading(false);
      }
    };
    fetchCustomers();
  }, []);

  const handleDelete = async (id) => {
    try {
      await potentialCustomersAPI.delete(id);
      setCustomers(customers.filter(c => c.id !== id));
    } catch (error) {
    }
  };

  const handleEdit = (customer) => {
    setEditingCustomer(customer);
    setEditName(customer.name);
    setEditEmail(customer.email);
    setEditPhone(customer.phone);
    setEditFollowUpStatus(customer.status);
    setEditNotes(customer.notes);
    setIsEditModalOpen(true);
  };

  const handleUpdateCustomer = async () => {
    try {
      const statusTypeMap = {
        'Pending Payment': 'payment',
        'Follow up in a week': 'followup',
        'Follow up in 2 days': 'followup',
        'Contacted': 'contacted',
        'Not Interested': 'contacted'
      };
      
      const updatedData = {
        name: editName,
        email: editEmail,
        phone: editPhone,
        status: editFollowUpStatus,
        status_type: statusTypeMap[editFollowUpStatus] || 'followup',
        notes: editNotes
      };
      
      const response = await potentialCustomersAPI.update(editingCustomer.id, updatedData);
      setCustomers(customers.map(c => 
        c.id === editingCustomer.id ? { ...c, ...updatedData } : c
      ));
      setIsEditModalOpen(false);
      setEditingCustomer(null);
    } catch (error) {
    }
  };

  const handleCreateCustomer = async () => {
    try {
      const statusTypeMap = {
        'Pending Payment': 'payment',
        'Follow up in a week': 'followup',
        'Follow up in 2 days': 'followup',
        'Contacted': 'contacted',
        'Not Interested': 'contacted'
      };
      
      const newCustomer = {
        name,
        email,
        phone,
        status: followUpStatus,
        status_type: statusTypeMap[followUpStatus] || 'followup',
        notes
      };
      const response = await potentialCustomersAPI.create(newCustomer);
      setCustomers([response.data, ...customers]);
      setIsAddModalOpen(false);
      resetAddForm();
    } catch (error) {
    }
  };

  const resetAddForm = () => {
    setName('');
    setEmail('');
    setPhone('');
    setFollowUpStatus('');
    setNotes('');
  };

  const getStatusColor = (statusType) => {
    switch (statusType) {
      case 'followup':
        return 'bg-orange-100 text-orange-700';
      case 'payment':
        return 'bg-amber-100 text-amber-700';
      case 'contacted':
        return 'bg-gray-100 text-gray-600';
      default:
        return 'bg-blue-100 text-blue-700';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="animate-spin text-[#4F46E5]" size={32} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Potential Customers</h1>
          <p className="text-gray-500">Manage leads and potential customers</p>
        </div>
        <Button 
          onClick={() => setIsAddModalOpen(true)}
          className="bg-[#4F46E5] hover:bg-[#4338CA] text-white h-10"
        >
          <Plus size={16} className="mr-2" />
          Add Customer
        </Button>
      </div>

      {/* Customers Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {customers.map((customer) => (
          <Card key={customer.id} className="p-6 bg-white border border-gray-100">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center">
                  <User className="text-indigo-500" size={24} />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">{customer.name}</h3>
                  <span className={`inline-block px-2.5 py-0.5 text-xs font-medium rounded-full mt-1 ${getStatusColor(customer.status_type)}`}>
                    {customer.status}
                  </span>
                </div>
              </div>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="p-1 hover:bg-gray-100 rounded transition-colors">
                    <MoreHorizontal className="text-gray-400" size={18} />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => handleEdit(customer)}>
                    <Pencil className="mr-2 text-gray-500" size={14} />
                    Edit
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    onClick={() => handleDelete(customer.id)}
                    className="text-red-500 focus:text-red-500"
                  >
                    <Trash2 className="mr-2" size={14} />
                    Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            <div className="space-y-3">
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Mail size={14} className="text-gray-400" />
                <span>{customer.email}</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Phone size={14} className="text-gray-400" />
                <span>{customer.phone}</span>
              </div>
            </div>

            <div className="mt-4 pt-4 border-t border-gray-100">
              <p className="text-sm text-gray-600">{customer.notes}</p>
            </div>
          </Card>
        ))}
      </div>

      {customers.length === 0 && (
        <Card className="p-8 bg-white border border-gray-100 text-center">
          <p className="text-gray-500">No potential customers found. Click "Add Customer" to add your first lead.</p>
        </Card>
      )}

      {/* Add Customer Modal */}
      <Dialog open={isAddModalOpen} onOpenChange={setIsAddModalOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Add Potential Customer</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Name *</Label>
              <Input
                placeholder="John Doe"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label>Email</Label>
              <Input
                type="email"
                placeholder="john@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label>Phone</Label>
              <Input
                placeholder="+1234567890"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label>Follow-up Status</Label>
              <Select value={followUpStatus} onValueChange={setFollowUpStatus}>
                <SelectTrigger>
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Pending Payment">Pending Payment</SelectItem>
                  <SelectItem value="Follow up in a week">Follow up in a week</SelectItem>
                  <SelectItem value="Follow up in 2 days">Follow up in 2 days</SelectItem>
                  <SelectItem value="Contacted">Contacted</SelectItem>
                  <SelectItem value="Not Interested">Not Interested</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                placeholder="Add notes about this lead..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
              />
            </div>

            <Button
              onClick={handleCreateCustomer}
              className="w-full bg-[#4F46E5] hover:bg-[#4338CA] text-white"
              disabled={!name || !email}
            >
              Add Customer
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Customer Modal */}
      <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Edit Customer</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Name *</Label>
              <Input
                placeholder="John Doe"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label>Email</Label>
              <Input
                type="email"
                placeholder="john@example.com"
                value={editEmail}
                onChange={(e) => setEditEmail(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label>Phone</Label>
              <Input
                placeholder="+1234567890"
                value={editPhone}
                onChange={(e) => setEditPhone(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label>Follow-up Status</Label>
              <Select value={editFollowUpStatus} onValueChange={setEditFollowUpStatus}>
                <SelectTrigger>
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Pending Payment">Pending Payment</SelectItem>
                  <SelectItem value="Follow up in a week">Follow up in a week</SelectItem>
                  <SelectItem value="Follow up in 2 days">Follow up in 2 days</SelectItem>
                  <SelectItem value="Contacted">Contacted</SelectItem>
                  <SelectItem value="Not Interested">Not Interested</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                placeholder="Add notes about this lead..."
                value={editNotes}
                onChange={(e) => setEditNotes(e.target.value)}
                rows={3}
              />
            </div>

            <Button
              onClick={handleUpdateCustomer}
              className="w-full bg-[#4F46E5] hover:bg-[#4338CA] text-white"
              disabled={!editName || !editEmail}
            >
              Update Customer
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PotentialCustomersPage;
